import { Component, HostBinding, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Fixture, FixturePage } from '@cds/betting-offer';
import { LiveSectionConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { EventFactory } from '@frontend/sports/grid/core/feature/factories';
import { GridGrouping, GridSorting } from '@frontend/sports/grid/core/feature/model';
import { GridOption, ObservableGrid, ObservableGridFactory } from '@frontend/sports/grid/core/feature/observables';
import { getEvents } from '@frontend/sports/grid/core/feature/store';
import { sum } from 'lodash-es';
import { finalize, map, mergeMap, take } from 'rxjs';

import { TabBarItem } from '../tab-bar/tab-bar.models';
import { LiveEventListService, LiveSportOverviewModel } from './live-event-list.service';
import { LiveUpcomingListComponent, UpcomingLoader, UpcomingModel, UpcomingMoreLoader } from './live-upcoming-list.component';

@HooksWireup()
@Component({
    selector: 'ms-live-event-list',
    templateUrl: 'live-event-list.component.html',
    providers: [ObservableGridFactory],
    standalone: false,
})
export class LiveEventListComponent extends OnRouteResolve<LiveSportOverviewModel> {
    live?: ObservableGrid;
    upcoming?: UpcomingModel;
    upcomingLoaded: boolean;
    tracking = 'LivePage/Sport';
    main?: TabBarItem;
    collapsedChangedState = '';
    isLoading = false;
    eventsTotalCount = 0;
    private region?: number;
    private hasTournaments: boolean;
    sport?: number;
    private upcomingThreshold = 10;
    private league?: number;
    private isVirtual?: boolean;

    @HostBinding('class') className = 'live-event-list';
    @ViewChild(LiveUpcomingListComponent) upcomingList: LiveUpcomingListComponent;

    constructor(
        public sitecore: Sitecore,
        private liveEventListService: LiveEventListService,
        private observableGridFactory: ObservableGridFactory,
        private eventFactory: EventFactory,
        private liveConfig: LiveSectionConfig,
        route: ActivatedRoute,
    ) {
        super(route);
    }

    onRouteResolved(model: LiveSportOverviewModel): void {
        this.observableGridFactory.destroy();
        this.eventsTotalCount = 0;

        if (!model.page?.fixtures || model.page.fixtures.length === 0) {
            this.live = undefined;

            return;
        }

        let sortedBy;

        if (model.page.totalCompetitions && model.page.totalCompetitions > 1) {
            sortedBy = model.sorting;
        }

        const grouping = this.liveEventListService.getGridGrouping(model.page.fixtures, sortedBy);

        this.resetUpcoming();
        const options: GridOption = {
            sorting: sortedBy,
            grouping,
        };

        this.sport = model.selectedSportId;
        this.region = model.selectedRegionId;
        this.hasTournaments = model.hasTournaments;
        this.league = model.selectedLeagueId;
        this.isVirtual = model.isVirtual;

        const liveGrid = this.observableGridFactory.fromResponse(this.getGridId(), model.page.fixtures, options);

        if (this.region && this.region > 0 && model.regions.length > 1) {
            const sport = model.sports.find((current) => current.id === this.sport)!;

            this.main = this.liveEventListService.getRegionTabs(model.regions, sport, this.region)[0]!;
            this.main.count = sport.counts.live;
            this.main.title = sport.name;
        } else {
            this.main = undefined;
        }

        this.eventsTotalCount = model.page.totalCount;

        if (liveGrid) {
            if (!this.live) {
                this.live = liveGrid;
            }

            liveGrid.pipe(take(1)).subscribe(() => {
                this.live = liveGrid;

                const visibleEvents = this.getExpandedEventCount(this.live);
                if (visibleEvents < this.upcomingThreshold) {
                    this.showUpcoming(true);
                }
            });
        } else {
            this.live = undefined;
            this.resetUpcoming();
        }
    }

    private addEvents(fixtures: Fixture[]): void {
        if (!this.live) {
            return;
        }

        const liveGrid = this.live.getState();

        const gridEvents = getEvents(liveGrid);
        const allEvents = [...gridEvents, ...fixtures];

        const grouping = this.liveEventListService.getGridGrouping(allEvents, liveGrid.sorting);

        this.live.addEvents(fixtures, undefined, grouping);
    }

    private hasEvents(page?: FixturePage): boolean {
        return !!page && !!page.fixtures && page.fixtures.length > 0;
    }

    private getEvents(next: (page: FixturePage) => void): void {
        this.isLoading = true;

        const sorting = this.live?.getState().sorting || GridSorting.Competition;

        this.liveEventListService
            .getFixtures({
                sportId: this.sport || 0,
                regionId: this.getRegionId(),
                tournamentId: this.getTournamentId(),
                sorting,
                skip: this.eventCount,
                take: this.liveConfig.pageSize,
            })
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe((result) => {
                if (result && this.hasEvents(result)) {
                    next(result);
                }
            });
    }

    get eventCount(): number {
        if (!this.live) {
            return 0;
        }
        const grid = this.live.getState();

        if (!grid) {
            return 0;
        }

        return getEvents(grid).length;
    }

    get showMore(): boolean {
        return this.eventsTotalCount > this.eventCount;
    }

    scrolledBottom(): void {
        this.showUpcoming();

        if (!this.live || !this.showMore || this.isLoading) {
            return;
        }

        this.getEvents((data) => this.addEvents(data.fixtures));
    }

    showUpcoming(forceLoad?: boolean): void {
        if (!forceLoad && this.upcomingLoaded) {
            return;
        }

        this.upcomingLoaded = true;
        this.upcoming = {
            load: this.loadUpcoming,
            more: this.moreUpcoming,
        };
    }

    liveGroupToggled(): void {
        if (!this.live) {
            return;
        }

        const visibleEvents = this.getExpandedEventCount(this.live);
        if (visibleEvents < this.upcomingThreshold) {
            this.showUpcoming();
        }
    }

    private getGridId(upcoming?: boolean): string {
        const liveKey = this.region ? `${this.sport}/live/regions/${this.region}` : `${this.sport}/live`;

        return upcoming ? liveKey + '/upcoming' : liveKey;
    }

    private getExpandedEventCount(grid: ObservableGrid): number {
        const state = grid.getState();

        return sum(state.groups.filter((g) => !g.collapsed).map((g) => g.events.length));
    }

    private resetUpcoming(): void {
        this.upcomingLoaded = false;
        this.upcoming = undefined;
    }

    private loadUpcoming: UpcomingLoader = () =>
        this.liveEventListService
            .getUpcomingFixtures({
                sportId: this.sport,
                regionId: this.getRegionId(),
                tournamentId: this.getTournamentId(),
                leagueId: this.league,
                isVirtual: this.isVirtual,
            })
            .pipe(
                map((page) => {
                    if (!page?.fixtures?.length) {
                        return [];
                    }

                    return [
                        {
                            grid: this.observableGridFactory.fromResponse(this.getGridId(true), page.fixtures, {
                                grouping: GridGrouping.None,
                            }),
                            count: page.totalCount,
                        },
                    ];
                }),
            );

    private moreUpcoming: UpcomingMoreLoader = (skip) =>
        this.liveEventListService
            .getUpcomingFixtures({
                sportId: this.sport,
                regionId: this.getRegionId(),
                tournamentId: this.getTournamentId(),
                skip,
                leagueId: this.league,
                isVirtual: this.isVirtual,
            })
            .pipe(mergeMap((page) => this.eventFactory.create(page?.fixtures)));

    private getRegionId(): number | undefined {
        return this.hasTournaments ? undefined : this.region;
    }

    private getTournamentId(): number | undefined {
        return this.hasTournaments ? this.region : undefined;
    }
}
